import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Search, Plus, Minus, Trash2, ShoppingBag, Check, Loader2, AlertTriangle, Store, Package, Receipt, Users, X } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { usePOSRetailProducts } from '../../lib/menu-hooks';
import { useTenant } from '../../lib/tenant-context';
import { supabase } from '../../lib/supabase';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatCurrency, cn } from '../../lib/utils';
import { PayTableModal } from '../../components/admin/PayTableModal';
import type { TableWithOrder } from '../../lib/types';
interface CartLine {
  id: string;
  menuItemId: string;
  code: string;
  name: string;
  unitPrice: number;
  quantity: number;
  stock?: number | null;
}
interface ActiveTable {
  orderId: string;
  tableNumber: string | number | null;
  customerName: string | null;
  total: number | null;
}
export function POSCajaPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const tenantCtx = useTenant();
  const isNatuCafe = String(restaurant?.id) === '19' || restaurant?.slug === 'natucafe-coffee-shop';
  const {
    items,
    loading: itemsLoading,
    refetch
  } = usePOSRetailProducts(restaurant?.id ? String(restaurant.id) : undefined);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState<CartLine[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  // Active-table picker
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [activeTables, setActiveTables] = useState<ActiveTable[]>([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  // ─── Pay modal (Venta rápida con factura/efectivo/tarjeta/SINPE) ──
  // En vez de insertar la orden ya cobrada (lo que rompía contra
  // orders_status_check), creamos la orden en status='pending' y
  // abrimos PayTableModal — el mismo modal de cerrar mesa, con
  // selección de método de pago + opción de factura. processPayment
  // se encarga de mover la orden a status='delivered' (canónico) y
  // de disparar el print job de factura para NatuCafé.
  const [payTable, setPayTable] = useState<TableWithOrder | null>(null);
  const [showPayModal, setShowPayModal] = useState(false);
  // ─── Search filter ────────────────────────────────────────
  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it: any) => {
      const name = (it.name || '').toLowerCase();
      const code = String(it.external_pos_id ?? '').toLowerCase();
      return name.includes(q) || code.includes(q);
    });
  }, [items, search]);
  // ─── Cart helpers ─────────────────────────────────────────
  const addToCart = (item: any) => {
    setError(null);
    setSuccessMsg(null);
    setCart(prev => {
      const existing = prev.find(l => l.menuItemId === String(item.id));
      if (existing) {
        return prev.map(l => l.id === existing.id ? {
          ...l,
          quantity: l.quantity + 1
        } : l);
      }
      return [...prev, {
        id: `${item.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        menuItemId: String(item.id),
        code: String(item.external_pos_id ?? ''),
        name: item.name,
        unitPrice: Number(item.price) || 0,
        quantity: 1,
        stock: item.stock_quantity === null || item.stock_quantity === undefined ? null : Number(item.stock_quantity)
      }];
    });
  };
  const updateQty = (lineId: string, delta: number) => {
    setCart(prev => prev.map(l => l.id === lineId ? {
      ...l,
      quantity: Math.max(0, l.quantity + delta)
    } : l).filter(l => l.quantity > 0));
  };
  const removeLine = (lineId: string) => {
    setCart(prev => prev.filter(l => l.id !== lineId));
  };
  const cartTotal = useMemo(() => cart.reduce((s, l) => s + l.unitPrice * l.quantity, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((s, l) => s + l.quantity, 0), [cart]);
  // ─── Quick sale: create a new order ───────────────────────
  const handleQuickSale = async () => {
    if (!restaurant?.id) return;
    if (cart.length === 0) {
      setError('Agrega al menos un producto al carrito.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const subtotal = cartTotal;
      const trackingToken = typeof crypto !== 'undefined' && (crypto as any).randomUUID ? (crypto as any).randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      // Crear la orden en status='pending' (valor canónico del
      // check constraint orders_status_check). El cobro se hace
      // adentro de PayTableModal — ahí processPayment la mueve a
      // 'delivered' al insertar el payment. source='pos_quick_sale'
      // marca la venta de mostrador (el trigger de print de cocina
      // ignora estas órdenes; sólo se imprime la factura al cobrar).
      const {
        data: orderRow,
        error: orderErr
      } = await supabase.from('orders').insert({
        restaurant_id: Number(restaurant.id),
        tracking_token: trackingToken,
        customer_name: 'Estantería',
        customer_phone: null,
        delivery_type: 'dine_in',
        payment_method: 'cash',
        status: 'pending',
        subtotal,
        delivery_fee: 0,
        total: subtotal,
        notes: 'Venta rápida Estantería',
        source: 'pos_quick_sale'
      }).select('id, order_number, tracking_token, restaurant_id, customer_name, customer_phone, delivery_type, payment_method, table_id, status, subtotal, delivery_fee, total, notes, source, created_at, updated_at').single();
      if (orderErr) throw orderErr;
      if (!orderRow?.id) throw new Error('No se pudo crear la orden.');
      // Insert order_items en 'pending' — el trigger de print de
      // cocina los ignora porque la orden tiene source='pos_quick_sale'
      // (guard en SETUP_NATUCAFE_PRINT_ON_ITEMS_TRIGGER.sql).
      const itemsPayload = cart.map(l => ({
        order_id: orderRow.id,
        restaurant_id: Number(restaurant.id),
        menu_item_id: l.menuItemId,
        item_name_snapshot: l.name,
        unit_price_snapshot: l.unitPrice,
        quantity: l.quantity,
        line_total: l.unitPrice * l.quantity,
        status: 'pending',
        notes: l.code ? `Cód. ${l.code}` : null
      }));
      const {
        error: itemsErr
      } = await supabase.from('order_items').insert(itemsPayload);
      if (itemsErr) throw itemsErr;
      // Construir TableWithOrder sintético — PayTableModal acepta
      // este shape (mismo patrón que CajaOverviewPage para órdenes
      // marketplace sin mesa real). table.id=null hace que el modal
      // cargue items vía orderId fallback.
      const syntheticTable: TableWithOrder = {
        id: '' as any,
        restaurant_id: String(restaurant.id),
        table_number: 'Estantería' as any,
        table_name: 'Estantería',
        zone: null,
        capacity: null,
        qr_code_url: null,
        qr_token: null,
        is_active: true,
        created_at: '',
        updated_at: '',
        current_order: {
          id: String(orderRow.id),
          restaurant_id: String(restaurant.id),
          order_number: String(orderRow.order_number || ''),
          tracking_token: orderRow.tracking_token,
          customer_name: orderRow.customer_name || 'Estantería',
          customer_phone: '',
          delivery_type: 'dine_in' as any,
          payment_method: 'cash',
          table_id: null,
          delivery_address: null,
          delivery_lat: null,
          delivery_lng: null,
          maps_url: null,
          subtotal: Number(orderRow.subtotal) || 0,
          delivery_fee: Number(orderRow.delivery_fee) || 0,
          total: Number(orderRow.total) || 0,
          status: 'pending' as any,
          notes: orderRow.notes,
          source: 'pos_quick_sale',
          ticket_image_url: null,
          created_at: orderRow.created_at || new Date().toISOString(),
          updated_at: orderRow.updated_at || new Date().toISOString()
        },
        current_order_items: [],
        status: 'occupied',
        order_channel: 'cashier',
        time_open_minutes: 0,
        item_count: cart.length
      } as TableWithOrder;
      setPayTable(syntheticTable);
      setShowPayModal(true);
    } catch (e: any) {
      console.error('[POS/Caja] quick sale error:', e);
      setError(e?.message || 'No se pudo registrar la venta.');
    } finally {
      setSubmitting(false);
    }
  };
  // ─── PayTableModal callback ───────────────────────────────
  const handlePaySuccess = (_allPaid: boolean) => {
    // El modal cierra solo cuando el usuario lo cierra; acá sólo
    // limpiamos el carrito y refrescamos productos (stock).
    setCart([]);
    setSuccessMsg('Venta registrada correctamente.');
    refetch();
  };
  const handlePayClose = () => {
    setShowPayModal(false);
    setPayTable(null);
  };
  // ─── Active table picker ──────────────────────────────────
  const openTablePicker = async () => {
    if (!restaurant?.id) return;
    if (cart.length === 0) {
      setError('Agrega al menos un producto antes de elegir una mesa.');
      return;
    }
    setError(null);
    setSuccessMsg(null);
    setShowTablePicker(true);
    setTablesLoading(true);
    try {
      const {
        data,
        error: e
      } = await supabase.from('orders').select('id, order_number, table_number, customer_name, total, status').eq('restaurant_id', Number(restaurant.id)).eq('delivery_type', 'dine_in').not('table_number', 'is', null).not('status', 'in', '(cancelled,completed,paid,refunded)').order('created_at', {
        ascending: false
      });
      if (e) throw e;
      setActiveTables((data || []).map((r: any) => ({
        orderId: String(r.id),
        tableNumber: r.table_number,
        customerName: r.customer_name,
        total: r.total
      })));
    } catch (e: any) {
      console.error('[POS/Caja] active tables error:', e);
      setError(e?.message || 'No se pudieron cargar las mesas activas.');
      setActiveTables([]);
    } finally {
      setTablesLoading(false);
    }
  };
  const handleAddToTable = async (targetOrderId: string) => {
    if (!restaurant?.id) return;
    setSubmitting(true);
    setError(null);
    setSuccessMsg(null);
    try {
      // Insert order_items
      const itemsPayload = cart.map(l => ({
        order_id: targetOrderId,
        restaurant_id: Number(restaurant.id),
        menu_item_id: l.menuItemId,
        item_name_snapshot: l.name,
        unit_price_snapshot: l.unitPrice,
        quantity: l.quantity,
        line_total: l.unitPrice * l.quantity,
        status: 'pending',
        notes: l.code ? `Cód. ${l.code}` : null
      }));
      const {
        error: itemsErr
      } = await supabase.from('order_items').insert(itemsPayload);
      if (itemsErr) throw itemsErr;
      // Recompute order totals
      const {
        data: allItems,
        error: fetchErr
      } = await supabase.from('order_items').select('line_total, status').eq('order_id', targetOrderId);
      if (fetchErr) throw fetchErr;
      const newSubtotal = (allItems || []).filter((it: any) => it.status !== 'cancelled').reduce((s: number, it: any) => s + (Number(it.line_total) || 0), 0);
      const {
        data: orderRow
      } = await supabase.from('orders').select('delivery_fee').eq('id', targetOrderId).maybeSingle();
      const deliveryFee = Number(orderRow?.delivery_fee) || 0;
      const {
        error: updErr
      } = await supabase.from('orders').update({
        subtotal: newSubtotal,
        total: newSubtotal + deliveryFee,
        updated_at: new Date().toISOString()
      }).eq('id', targetOrderId);
      if (updErr) throw updErr;
      setSuccessMsg('Productos agregados a la mesa.');
      setCart([]);
      setShowTablePicker(false);
      refetch();
    } catch (e: any) {
      console.error('[POS/Caja] add to table error:', e);
      setError(e?.message || 'No se pudieron agregar los productos a la mesa.');
    } finally {
      setSubmitting(false);
    }
  };
  // ─── Loading / gating ─────────────────────────────────────
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-5364">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-5365" />
      </div>;
  }
  if (!isNatuCafe) {
    return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-5366">
        <div className="p-6 flex flex-col items-center justify-center min-h-[50vh] text-center" data-id="element-5367">
          <Store className="w-16 h-16 text-gray-300 mb-4" data-id="element-5368" />
          <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-5369">
            Módulo no disponible
          </h2>
          <p className="text-gray-500" data-id="element-5370">
            Este módulo es exclusivo para NatuCafé.
          </p>
        </div>
      </AdminLayout>;
  }
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-5371">
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4" data-id="element-5372">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-5373">
          <div data-id="element-5374">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-5375">Estantería</h1>
            <p className="text-sm text-gray-500" data-id="element-5376">
              Venta rápida de productos · {items.length} disponibles
            </p>
          </div>
        </div>

        {/* Banners */}
        {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2" data-id="element-5377">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-5378" />
            <span data-id="element-5379">{error}</span>
          </div>}
        {successMsg && <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 flex items-start gap-2" data-id="element-5380">
            <Check className="w-4 h-4 shrink-0 mt-0.5" data-id="element-5381" />
            <span data-id="element-5382">{successMsg}</span>
          </div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-5383">
          {/* LEFT: products */}
          <div className="lg:col-span-2 space-y-3" data-id="element-5384">
            {/* Big search */}
            <div className="relative" data-id="element-5385">
              <Search className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-5386" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por código o nombre… (ej: 1001, miel, café)" autoFocus className="w-full pl-12 pr-4 h-14 rounded-2xl border-2 border-gray-200 text-base font-medium focus:outline-none focus:border-amber-500 bg-white shadow-sm" data-id="element-5387" />
              {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400" aria-label="Limpiar búsqueda" data-id="element-5388">
                  <X className="w-4 h-4" data-id="element-5389" />
                </button>}
            </div>

            {/* Product grid */}
            <div className="bg-white rounded-2xl border border-gray-200 p-3 min-h-[300px]" data-id="element-5390">
              {itemsLoading ? <div className="flex justify-center py-16 text-gray-400" data-id="element-5391">
                  <Loader2 className="w-6 h-6 animate-spin" data-id="element-5392" />
                </div> : visibleItems.length === 0 ? <div className="text-center py-16 text-gray-500" data-id="element-5393">
                  <Package className="w-10 h-10 mx-auto mb-2 text-gray-300" data-id="element-5394" />
                  <p className="text-sm font-medium" data-id="element-5395">
                    {search ? 'Sin resultados para esa búsqueda.' : 'No hay productos de Estantería cargados.'}
                  </p>
                </div> : <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-5396">
                  {visibleItems.map((item: any) => {
                const stock = item.stock_quantity === null || item.stock_quantity === undefined ? null : Number(item.stock_quantity);
                const lowStock = stock !== null && stock <= 0;
                return <div key={item.id} className="p-3 rounded-xl border border-gray-200 flex flex-col gap-2 bg-white" data-id="element-5397">
                        <div className="flex items-start justify-between gap-2" data-id="element-5398">
                          <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded tabular-nums" data-id="element-5399">
                            {item.external_pos_id || '—'}
                          </span>
                          {stock !== null && <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded tabular-nums', lowStock ? 'text-red-700 bg-red-50' : 'text-emerald-700 bg-emerald-50')} data-id="element-5400">
                              Stock {stock}
                            </span>}
                        </div>
                        <p className="font-bold text-sm text-gray-900 leading-snug line-clamp-2 min-h-[2.5rem]" data-id="element-5401">
                          {item.name}
                        </p>
                        <p className="text-base font-black text-gray-900 tabular-nums" data-id="element-5402">
                          {formatCurrency(item.price)}
                        </p>
                        <button onClick={() => addToCart(item)} className="mt-auto h-9 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm flex items-center justify-center gap-1 active:scale-95 transition-all" data-id="element-5403">
                          <Plus className="w-4 h-4" data-id="element-5404" />
                          Agregar
                        </button>
                      </div>;
              })}
                </div>}
            </div>
          </div>

          {/* RIGHT: cart */}
          <div className="bg-white rounded-2xl border border-gray-200 flex flex-col max-h-[calc(100vh-180px)] sticky top-4" data-id="element-5405">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between" data-id="element-5406">
              <div className="flex items-center gap-2" data-id="element-5407">
                <ShoppingBag className="w-4 h-4 text-gray-700" data-id="element-5408" />
                <p className="font-bold text-sm text-gray-900" data-id="element-5409">
                  Carrito ({cartCount})
                </p>
              </div>
              {cart.length > 0 && <button onClick={() => setCart([])} className="text-xs font-bold text-red-600 hover:text-red-700" data-id="element-5410">
                  Vaciar
                </button>}
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2" data-id="element-5411">
              {cart.length === 0 ? <div className="text-center py-12 text-gray-400 text-sm" data-id="element-5412">
                  Selecciona productos para iniciar una venta.
                </div> : cart.map(l => <div key={l.id} className="rounded-lg border border-gray-200 px-3 py-2" data-id="element-5413">
                    <div className="flex items-start justify-between gap-2 mb-1.5" data-id="element-5414">
                      <div className="min-w-0" data-id="element-5415">
                        {l.code && <span className="text-[10px] font-bold text-amber-700 tabular-nums" data-id="element-5416">
                            {l.code}
                          </span>}
                        <p className="text-sm font-bold text-gray-900 leading-tight truncate" data-id="element-5417">
                          {l.name}
                        </p>
                      </div>
                      <button onClick={() => removeLine(l.id)} className="text-red-500 hover:bg-red-50 w-7 h-7 rounded-md flex items-center justify-center shrink-0" aria-label="Quitar" data-id="element-5418">
                        <Trash2 className="w-3.5 h-3.5" data-id="element-5419" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-5420">
                      <div className="flex items-center gap-1" data-id="element-5421">
                        <button onClick={() => updateQty(l.id, -1)} className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700" data-id="element-5422">
                          <Minus className="w-3.5 h-3.5" data-id="element-5423" />
                        </button>
                        <span className="w-7 text-center font-black text-sm text-gray-900 tabular-nums" data-id="element-5424">
                          {l.quantity}
                        </span>
                        <button onClick={() => updateQty(l.id, +1)} className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700" data-id="element-5425">
                          <Plus className="w-3.5 h-3.5" data-id="element-5426" />
                        </button>
                      </div>
                      <span className="text-sm font-black text-gray-900 tabular-nums" data-id="element-5427">
                        {formatCurrency(l.unitPrice * l.quantity)}
                      </span>
                    </div>
                  </div>)}
            </div>

            <div className="border-t border-gray-100 p-4 space-y-3 bg-gray-50 rounded-b-2xl" data-id="element-5428">
              <div className="flex items-center justify-between" data-id="element-5429">
                <span className="text-sm font-bold text-gray-600" data-id="element-5430">Total</span>
                <span className="text-2xl font-black text-gray-900 tabular-nums" data-id="element-5431">
                  {formatCurrency(cartTotal)}
                </span>
              </div>
              <button onClick={handleQuickSale} disabled={submitting || cart.length === 0} className="w-full h-12 rounded-xl font-black text-sm bg-amber-500 hover:bg-amber-600 text-white active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-5432">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-5433" /> : <>
                    <Receipt className="w-5 h-5" data-id="element-5434" />
                    Venta rápida
                  </>}
              </button>
              <button onClick={openTablePicker} disabled={submitting || cart.length === 0} className="w-full h-11 rounded-xl font-bold text-sm bg-white border-2 border-gray-200 hover:border-gray-300 text-gray-900 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-5435">
                <Users className="w-4 h-4" data-id="element-5436" />
                Agregar a mesa activa
              </button>
            </div>
          </div>
        </div>

        {/* Active table picker modal */}
        {showTablePicker && <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => !submitting && setShowTablePicker(false)} data-id="element-5437">
            <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[80vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-5438">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-5439">
                <div data-id="element-5440">
                  <h3 className="font-black text-gray-900" data-id="element-5441">Mesas activas</h3>
                  <p className="text-xs text-gray-500" data-id="element-5442">
                    Selecciona una mesa para sumar los productos.
                  </p>
                </div>
                <button onClick={() => setShowTablePicker(false)} disabled={submitting} className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500" data-id="element-5443">
                  <X className="w-5 h-5" data-id="element-5444" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3" data-id="element-5445">
                {tablesLoading ? <div className="flex justify-center py-12 text-gray-400" data-id="element-5446">
                    <Loader2 className="w-6 h-6 animate-spin" data-id="element-5447" />
                  </div> : activeTables.length === 0 ? <div className="text-center py-12 text-gray-500 text-sm" data-id="element-5448">
                    No hay mesas con orden abierta.
                  </div> : <div className="space-y-2" data-id="element-5449">
                    {activeTables.map(t => <button key={t.orderId} onClick={() => handleAddToTable(t.orderId)} disabled={submitting} className="w-full text-left p-3 rounded-xl border border-gray-200 hover:border-amber-400 hover:bg-amber-50/40 active:scale-[0.99] transition-all flex items-center justify-between gap-3 disabled:opacity-40" data-id="element-5450">
                        <div className="min-w-0" data-id="element-5451">
                          <p className="font-bold text-sm text-gray-900" data-id="element-5452">
                            Mesa {t.tableNumber ?? '—'}
                          </p>
                          {t.customerName && <p className="text-xs text-gray-500 truncate" data-id="element-5453">
                              {t.customerName}
                            </p>}
                        </div>
                        {t.total !== null && <span className="text-sm font-black text-gray-900 tabular-nums shrink-0" data-id="element-5454">
                            {formatCurrency(Number(t.total) || 0)}
                          </span>}
                      </button>)}
                  </div>}
              </div>
            </div>
          </div>}

        {/* Pay modal — Venta rápida con factura/método de pago */}
        <PayTableModal isOpen={showPayModal} onClose={handlePayClose} table={payTable} restaurantId={restaurant?.id} onSuccess={handlePaySuccess} data-id="element-5455" />
      </div>
    </AdminLayout>;
}